import React from "react";
import {
	SEGMENT_BZA,
	URL_BZA_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
	URL_BZA_AIRCRAFT_DATA,
	URL_BZA_AIRCRAFT_HISTORY_DATA,
	URL_BZA_OPERATOR_AIRPORT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productSegmentPageProps } from "../../common/types/product";

export default function BusinessJetAircraftOwnershipData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Access detailed data on business jet aircraft owners, managers, and SPVs, on a tail level."
				packages={[
					{
						label: SEGMENT_BZA,
						features: [
							"Insights into business jet aircraft owners and/or managers worldwide",
							"Overview of the SPVs and the specific aircraft located within",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft Profile",
						description:
							"Get an overview of aircraft ownership data on a tail level, including:",
						list: [
							"Aircraft owner",
							"Aircraft manager",
							"Ownership type",
							"SPV",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-ownership-data/aircraft-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for business jet aircraft owners, managers, and SPVs on a tail level within the Business Jet Aircraft Data search."
				slides={[
					{
						title: "Ownership",
						description:
							"Search for ownership information within the Business Jet Aircraft Data tab by:",
						list: [
							"SPV/trust name",
							"Owner/manager",
							"Owner/Manager selection (choose to search by owner, manager, or both)",
							"Ownership type (company-owned, Biz-Av managed, etc.)",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-ownership-data/aircraft-search-by-continent.jpg",
							title: "List of all Biz-Av managed business jets in North America",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 44,000 business jet operators and over 14,000 airport profiles.",
						url: URL_BZA_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Business Jet Aircraft Data",
						content:
							"Get access to more than 29,000 business jets worldwide, with detailed insights at the tail level.",
						url: URL_BZA_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft Accidents and Incidents Data",
						content:
							"Get a detailed overview of a business jet aircraft accident/incident on a tail level.",
						url: URL_BZA_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_BZA_AIRCRAFT_HISTORY_DATA,
					},
				]}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news and data for 14 days. Our no-commitment guarantee."
					subscribeLink={subscribeLink}
				/>
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Insights into business jet aircraft owners and/or managers worldwide",
					"Overview of the SPVs and the specific aircraft located within",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="The Business Jet Aircraft Ownership Data is available through the Business Jet Aircraft Data downloads and can be accessed instantly with unlimited downloads in Excel, CSV, or JSON formats."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Business Jet Aircraft Ownership Data is available through the Business Jet Aircraft Data downloads and can be accessed instantly with unlimited downloads in Excel, CSV, or JSON formats. For more information about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Business Jet Aircraft Ownership Data can be
								purchased as a data feed for seamless
								integration into your system. ch-aviation also
								provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How often is the data updated?",
						content:
							"The Business Jet Aircraft Ownership Data is being updated weekly, ensuring you always have the latest information.",
					},
					{
						title: "What does SPV stand for?",
						content: "SPV stands for Special Purpose Vehicle.",
					},
				]}
			/>
		</>
	);
}
